import { api, post, type ApiEnvelope } from "@/lib/api";

const VIDEO_BY_EXT: Record<string, string> = {
  mp4: "video/mp4",
  m4v: "video/mp4",
  webm: "video/webm",
  mov: "video/quicktime",
  qt: "video/quicktime",
  ogv: "video/ogg",
  ogg: "video/ogg",
  mkv: "video/x-matroska",
  avi: "video/x-msvideo",
  "3gp": "video/3gpp",
};

const AUDIO_BY_EXT: Record<string, string> = {
  mp3: "audio/mpeg",
  m4a: "audio/mp4",
  aac: "audio/aac",
  wav: "audio/wav",
  oga: "audio/ogg",
  ogg: "audio/ogg",
  flac: "audio/flac",
  wma: "audio/x-ms-wma",
  weba: "audio/webm",
};

const IMAGE_BY_EXT: Record<string, string> = {
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  gif: "image/gif",
  webp: "image/webp",
  avif: "image/avif",
  svg: "image/svg+xml",
  bmp: "image/bmp",
};

export type MediaKind = "image" | "video" | "audio" | "file";

const FILE_BY_EXT: Record<string, string> = {
  pdf: "application/pdf",
  txt: "text/plain",
  html: "text/html",
  htm: "text/html",
  md: "text/markdown",
  doc: "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ppt: "application/vnd.ms-powerpoint",
  pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
};

/** Windows often leaves File.type empty. Infer from the name. */
export function mimeForUpload(file: File, kind: MediaKind): string {
  const raw = (file.type || "").trim().toLowerCase().split(";")[0];
  if (kind === "video" && raw.startsWith("video/")) return raw;
  if (kind === "image" && raw.startsWith("image/")) return raw;
  if (kind === "audio" && raw.startsWith("audio/")) return raw;
  if (kind === "file" && (raw.startsWith("text/") || raw.startsWith("application/"))) return raw;
  const ext = file.name.split(".").pop()?.toLowerCase() || "";
  const inferred =
    kind === "video"
      ? VIDEO_BY_EXT[ext]
      : kind === "audio"
        ? AUDIO_BY_EXT[ext]
        : kind === "file"
          ? FILE_BY_EXT[ext]
          : IMAGE_BY_EXT[ext];
  return inferred || raw;
}

export type LearnMedia = {
  id: string;
  s3Key: string;
  url: string;
  mimeType: string;
  kind: MediaKind | "file";
  sizeBytes: number | null;
  originalFilename: string;
  createdAt: string;
};

type Presign = {
  presignedUrl: string;
  s3Key: string;
  bucket: string;
  region: string;
  url: string;
};

export async function listMyMedia(kind: MediaKind) {
  const envelope = await api<ApiEnvelope<LearnMedia[]>>(`/api/media?kind=${kind}`);
  return Array.isArray(envelope.data) ? envelope.data : [];
}

export async function uploadLearnMedia(file: File, kind: MediaKind): Promise<LearnMedia> {
  const mimeType = mimeForUpload(file, kind);
  const presign = await post<ApiEnvelope<Presign>>("/api/media/presign", {
    fileName: file.name,
    fileType: mimeType,
    kind,
  });
  if (!presign.success || !presign.data) {
    throw new Error(presign.message || "Could not prepare the upload");
  }
  const { presignedUrl, s3Key } = presign.data;
  const put = await fetch(presignedUrl, {
    method: "PUT",
    body: file,
    headers: { "Content-Type": mimeType },
  });
  if (!put.ok) throw new Error(`Upload failed (${put.status})`);
  const registered = await post<ApiEnvelope<LearnMedia>>("/api/media", {
    s3Key,
    mimeType,
    sizeBytes: file.size,
    originalFilename: file.name,
  });
  if (!registered.success || !registered.data) {
    throw new Error(registered.message || "Could not save the file");
  }
  return registered.data;
}

export type AnswerUpload = {
  id: string;
  name: string;
  url: string;
  s3Key: string;
  size: number;
  mime: string;
};

function mimeForAnswer(file: File) {
  return (
    mimeForUpload(file, "file") ||
    mimeForUpload(file, "image") ||
    mimeForUpload(file, "audio") ||
    mimeForUpload(file, "video") ||
    (file.type || "").trim()
  );
}

export async function uploadAnswerFile({
  activityId,
  questionId,
  submissionId,
  file,
  signal,
}: {
  activityId: string;
  questionId: string;
  submissionId?: string;
  file: File;
  signal?: AbortSignal;
}): Promise<AnswerUpload> {
  const mimeType = mimeForAnswer(file);
  const presign = await post<
    ApiEnvelope<{
      presignedUrl: string;
      s3Key: string;
      url: string;
      fileId: string;
      originalFilename?: string;
      mimeType?: string;
    }>
  >(`/api/student/activities/${encodeURIComponent(activityId)}/uploads/presign`, {
    questionId,
    submissionId,
    fileName: file.name,
    fileType: mimeType,
    sizeBytes: file.size,
  });
  if (!presign.success || !presign.data) {
    throw new Error(presign.message || "Could not prepare the upload");
  }
  if (signal?.aborted) {
    const err = new Error("Upload cancelled");
    err.name = "AbortError";
    throw err;
  }
  const { presignedUrl, s3Key, url, fileId } = presign.data;
  const put = await fetch(presignedUrl, {
    method: "PUT",
    body: file,
    headers: { "Content-Type": presign.data.mimeType || mimeType },
    signal,
  });
  if (!put.ok) throw new Error(`Upload failed (${put.status})`);
  return {
    id: fileId,
    name: presign.data.originalFilename || file.name,
    url,
    s3Key,
    size: file.size,
    mime: presign.data.mimeType || mimeType,
  };
}

export async function resolveCanvaShare(url: string) {
  const envelope = await post<ApiEnvelope<{ url: string; embed: string; id: string }>>("/api/media/canva-resolve", {
    url,
  });
  if (!envelope.success || !envelope.data?.url) {
    throw new Error(envelope.message || "Could not open that Canva link");
  }
  return envelope.data;
}
